export const normalizeEmail = (email = "") =>
  String(email).trim().toLowerCase();

export const normalizeYesNoRadioValue = (value) => {
  if (value === true || String(value || "").trim().toLowerCase() === "yes") {
    return "yes";
  }
  if (value === false || String(value || "").trim().toLowerCase() === "no") {
    return "no";
  }
  return "";
};

export const parseEmailList = (value = "") =>
  value
    .split(",")
    .map(normalizeEmail)
    .filter(Boolean);

export const isEmailInList = (email, emailList = []) =>
  emailList.includes(normalizeEmail(email));

export const RECRUITMENT_REFERENCING_EMAILS = Object.freeze(
  parseEmailList(process.env.REACT_APP_RECRUITMENT_REFERENCING_EMAILS),
);

export const SHOW_BEGIN_REFERENCING_FOR_ALL = false;
export const SHOW_CONTRACT_DRAFTING = false;

export const canUserBeginReferencing = (email) =>
  SHOW_BEGIN_REFERENCING_FOR_ALL ||
  isEmailInList(email, RECRUITMENT_REFERENCING_EMAILS);

export const RECRUITMENT_REFERENCE_STATUSES = Object.freeze([
  Object.freeze({
    id: "action-required",
    label: "Action Required",
    color: "crimson",
  }),
  Object.freeze({
    id: "awaiting-adviser",
    label: "Awaiting Adviser Confirmation",
    color: "red",
  }),
  Object.freeze({ id: "on-hold", label: "On Hold", color: "crimson" }),
  Object.freeze({
    id: "ready-to-request",
    label: "Ready to Request",
    color: "blue",
  }),
  Object.freeze({
    id: "reference-requested",
    label: "Reference Requested",
    color: "yellow",
    selectable: false,
  }),
  Object.freeze({
    id: "reference-sent",
    label: "Reference Requested",
    color: "yellow",
    selectable: false,
  }),
  Object.freeze({
    id: "follow-up-1-sent",
    label: "Follow Up 1 Sent",
    color: "purple",
    selectable: false,
  }),
  Object.freeze({
    id: "follow-up-2-sent",
    label: "Follow Up 2 Sent",
    color: "purple",
    selectable: false,
  }),
  Object.freeze({
    id: "follow-up-3-sent",
    label: "Follow Up 3 Sent",
    color: "purple",
    selectable: false,
  }),
  Object.freeze({
    id: "follow-up-due",
    label: "Follow Up Due",
    color: "purple",
    selectable: false,
  }),
  Object.freeze({
    id: "reference-received",
    label: "Reference Received",
    color: "green",
  }),
  Object.freeze({
    id: "not-required",
    label: "Not Required",
    color: "brandingOrange",
  }),
  Object.freeze({
    id: "stop-auto-chasing",
    label: "Stop Auto Chasing",
    color: "grey",
  }),
]);

export const RECRUITMENT_SELECTABLE_REFERENCE_STATUSES = Object.freeze(
  RECRUITMENT_REFERENCE_STATUSES.filter(
    (status) => status.selectable !== false,
  ),
);

export const REFERENCE_EMAIL_BLOCKED_STATUSES = Object.freeze([
  "action-required",
  "crimson",
  "awaiting-adviser",
  "awaiting-adviser-confirmation",
  "on-hold",
  "not-required",
  "received",
  "reference-received",
  "stop-auto-chasing",
]);

export const REFERENCE_AUTOMATION_STOPPED_STATUSES = Object.freeze([
  "action-required",
  "crimson",
  "ready-to-request",
  "complete",
  "completed",
  "received",
  "reference-received",
  ...REFERENCE_EMAIL_BLOCKED_STATUSES,
]);

export const normalizeReferenceStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");

  return normalized === "crimson" ? "action-required" : normalized;
};

const isAffirmative = (value) =>
  value === true || ["true", "yes"].includes(String(value || "").toLowerCase());

export const getApplicationReferenceEmailBlockReason = (record = {}) => {
  if (isAffirmative(record.isAppointed) || record.appointedAt) {
    return "The adviser is appointed, so reference emails and follow-ups are blocked.";
  }
  if (
    isAffirmative(record.notProceedingWith) ||
    isAffirmative(record.npw) ||
    record.npwAt
  ) {
    return "The adviser is marked as NPW, so reference emails and follow-ups are blocked.";
  }
  const decisionStatus = normalizeReferenceStatus(
    record.decision?.status || record.decisionStatus,
  );
  if (decisionStatus === "appointed") {
    return "The adviser is appointed, so reference emails and follow-ups are blocked.";
  }
  if (["npw", "not-proceeding", "not-proceeding-with"].includes(decisionStatus)) {
    return "The adviser is marked as NPW, so reference emails and follow-ups are blocked.";
  }
  return "";
};

export const canSendReferenceEmailForStatus = (status) =>
  Boolean(normalizeReferenceStatus(status)) &&
  !REFERENCE_EMAIL_BLOCKED_STATUSES.includes(normalizeReferenceStatus(status));

export const getReferenceAutomationMessage = ({
  status,
  adviserPermission,
  referenceSent,
  followUpTimeframe,
  skipReferenceFollowUp,
}) => {
  const normalizedStatus = normalizeReferenceStatus(status);
  const permissionGranted =
    adviserPermission === true ||
    String(adviserPermission || "").toLowerCase() === "yes";
  if (!permissionGranted) {
    return "The reference email will not be sent and automatic follow-ups will not run until adviser permission is set to Yes.";
  }
  if (!canSendReferenceEmailForStatus(normalizedStatus)) {
    return "The reference email will not be sent and automatic follow-ups will not run with the current status.";
  }
  if (REFERENCE_AUTOMATION_STOPPED_STATUSES.includes(normalizedStatus)) {
    if (normalizedStatus === "ready-to-request") {
      return referenceSent
        ? "Automatic follow-ups are paused while the reference has the current status."
        : "The initial reference email can be sent manually, but automatic follow-ups are paused while the reference has the current status.";
    }
    return ["received", "reference-received", "complete", "completed"].includes(normalizedStatus)
      ? "The reference is complete or has been received, so no automatic email will be sent and automatic follow-ups will not run."
      : "No email will be sent and automatic follow-ups will not run with the current status.";
  }
  if (!followUpTimeframe) {
    return referenceSent
      ? "The initial reference email has been sent. No automatic follow-ups are scheduled."
      : "Saving will not send an email. You can send a one-time reference request from View Request; no automatic follow-ups are scheduled.";
  }
  if (["weekly", "twice-weekly"].includes(followUpTimeframe)) {
    if (!referenceSent) {
      return `Saving will store the ${followUpTimeframe === "weekly" ? "weekly" : "twice-weekly"} schedule but will not send an email. Send the initial request from View Request; follow-ups will start from that send date.`;
    }
    return skipReferenceFollowUp
      ? "The initial reference email has been sent. The next automatic follow-up will be skipped, then the selected schedule will continue."
      : `The initial reference email has been sent and automatic follow-ups will run ${followUpTimeframe === "weekly" ? "weekly" : "twice a week"}.`;
  }
  if (followUpTimeframe === "set-date") {
    if (referenceSent && skipReferenceFollowUp) {
      return "The initial reference email has been sent. The selected follow-up will be skipped and no further automatic follow-up is scheduled.";
    }
    return referenceSent
      ? "The initial reference email has been sent. The next automatic follow-up will run on the selected date."
      : "Saving will not send an email. Send the initial request from View Request; the follow-up will then use the selected date.";
  }
  return "No automatic reference activity is scheduled.";
};

export const getReadyReferenceStatus = ({
  status,
  adviserPermission,
}) => {
  const normalizedStatus = normalizeReferenceStatus(status);
  const permissionGranted =
    adviserPermission === true ||
    String(adviserPermission || "").toLowerCase() === "yes";
  return permissionGranted && normalizedStatus === "awaiting-adviser"
    ? "ready-to-request"
    : status;
};

export const calculateReferenceFollowUpDate = (
  timeframe,
  fromDate = null,
  selectedDate = null,
) => {
  if (!timeframe) return null;
  if (timeframe === "set-date") return selectedDate || null;
  if (!fromDate) return null;
  const nextDate = new Date(fromDate);
  if (Number.isNaN(nextDate.getTime())) return null;
  const days = timeframe === "twice-weekly"
    ? nextDate.getUTCDay() === 4 ? 4 : 3
    : 7;
  nextDate.setUTCDate(nextDate.getUTCDate() + days);
  return nextDate;
};

export const PERSONAL_REFERENCE_QUESTIONS = Object.freeze([
  "What is your relationship to Adviser?",
  "How long you have known Adviser?",
  "Can you please confirm, to the best of your knowledge, what Adviser was doing between (Unemployment dates/self-employed dates)?",
  "In your experience with Adviser, have you found Adviser to be, reliable, patient and compassionate?",
  "How would you best describe Adviser?",
]);

export const TRADE_REFERENCE_QUESTIONS = Object.freeze([
  "How long have you traded with the adviser?",
  "Please describe the nature of your business relationship with the adviser.",
  "Were payments made within the agreed terms?",
  "Have you experienced any concerns while trading with the adviser?",
  "Would you recommend the adviser as a business contact?",
]);

const LEGACY_ACCOUNTANT_REFERENCE_QUESTIONS = Object.freeze([
  "How long have you acted as accountant for the adviser?",
  "Are the adviser’s tax affairs up to date?",
  "Are you aware of any concerns regarding the adviser’s financial conduct?",
  "Has the adviser met their financial obligations when due?",
  "Would you recommend the adviser as a fit and proper person?",
]);

export const ACCOUNTANT_REFERENCE_QUESTIONS = Object.freeze([
  "How long have you been an accountant for?",
  "How long have you been working with Adviser for?",
  "Can you confirm what Adviser was doing between this period (Dates)?",
]);

void LEGACY_ACCOUNTANT_REFERENCE_QUESTIONS;

export const EDUCATIONAL_REFERENCE_QUESTIONS = Object.freeze([
  "During which period(s) was Adviser Name enrolled at Educational Institute?",
  "In your opinion, did Adviser Name demonstrate honesty and integrity during his studies?",
  "Was the student ever involved in any disciplinary actions or matters of concern?",
  "How would you describe the student’s overall conduct and character?",
]);
